import { ref, onMounted, onUnmounted } from 'vue'

export default function useCamera(
	videoRef,
	config = { width: 1280, height: 720 }
) {
	const stream = ref(null)
	const error = ref(null)
	const isCameraActive = ref(false)
	const isFacingModeUser = ref(false) // true为前置摄像头，false为后置摄像头

	// 初始化摄像头
	const startCamera = async () => {
		try {
			// 停止现有流
			stopCamera()

			const constraints = {
				video: {
					facingMode: isFacingModeUser.value ? 'user' : 'environment',
					width: { ideal: config.width },
					height: { ideal: config.height }
				}
			}

			stream.value = await navigator.mediaDevices.getUserMedia(constraints)

			if (videoRef.value) {
				videoRef.value.srcObject = stream.value
				await videoRef.value.play()
				isCameraActive.value = true
				error.value = null
			}
		} catch (err) {
			error.value = `摄像头启动失败: ${err.message}`
			isCameraActive.value = false
		}
	}

	// 切换前后摄像头
	const toggleCamera = () => {
		isFacingModeUser.value = !isFacingModeUser.value
		if (isCameraActive.value) {
			startCamera()
		}
	}

	// 停止摄像头
	const stopCamera = () => {
		if (stream.value) {
			stream.value.getTracks().forEach((track) => track.stop())
			stream.value = null
			isCameraActive.value = false
		}
	}

	// 拍照并返回图片数据
	const takePhoto = () => {
		if (!videoRef.value || !isCameraActive.value) {
			error.value = '摄像头未启动'
			return null
		}

		const canvas = document.createElement('canvas')
		// 按比例截取画面
		drawCanvas(canvas, videoRef, config)

		if (!canvas.toBlob) {
			throw new Error('toBlob is not supported in this browser.')
		}

		return new Promise((resolve, reject) => {
			// 将 Canvas 内容转换为 Blob
			canvas.toBlob(function (blob) {
				if (!blob) {
					reject(new Error('Failed to create Blob from Canvas.'))
					return
				}
				// 创建 File 对象
				const file = new File([blob], 'canvas-image.png', { type: 'image/png' })
				resolve(file)
			}, 'image/png')
		})
	}

	// 生命周期管理
	onMounted(() => {
		if (videoRef.value) {
			startCamera()
		}
	})

	onUnmounted(() => {
		stopCamera()
	})

	return {
		stream,
		error,
		isCameraActive,
		isFacingModeUser,
		startCamera,
		toggleCamera,
		stopCamera,
		takePhoto
	}
}

function drawCanvas(canvas, videoRef, config) {
	const { width, height } = config
	const ratio = width / height
	const { videoWidth, videoHeight } = videoRef.value
	const videoRatio = videoWidth / videoHeight

	let scale, startX, startY, endX, endY, canvasWidth, canvasHeight
	if (videoRatio >= ratio) {
		scale = videoHeight / height
		startX = (videoWidth - width * scale) / 2
		endX = startX + width * scale
		startY = 0
		endY = videoHeight
		canvasWidth = width * scale
		canvasHeight = videoHeight
	} else {
		scale = videoWidth / width
		startX = 0
		endX = videoWidth
		startY = (videoHeight - height * scale) / 2
		endY = startY + height * scale
		canvasHeight = height * scale
		canvasWidth = videoWidth
	}
	canvas.width = canvasWidth
	canvas.height = canvasHeight
	const ctx = canvas.getContext('2d')
	ctx.drawImage(
		videoRef.value,
		startX,
		startY,
		endX,
		endY,
		0,
		0,
		canvasWidth,
		canvasHeight
	)
}
